import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { jwtDecode } from "jwt-decode";
import Header from "./header";
import Footer from "./footer";
import { setUser } from "../features/user/userSlice";
import { useToast } from "../hooks/useToast";

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { showToast, ToastContainer } = useToast();
  const [countdown, setCountdown] = useState(null);

  // helper: check if route should skip auth check
  const isPublicRoute = () => {
    const path = location.pathname.toLowerCase();
    return (
      path === "/" ||
      path === "/contact" ||
      path === "/about-us" ||
      path === "/pricing" ||
      path === "/pricing/compare" ||
      path === "/login" ||
      path === "/register" ||
      path === "/verify" ||
      path === "/forgot-password" ||
      path === "/verify-reset" ||
      path === "/reset-password" ||
      path.startsWith("/attend")
    );
  };

  // helper: check token validity
  const checkTokenValidity = () => {
    const token = sessionStorage.getItem("access_token");
    const userDetail = sessionStorage.getItem("userDetail");

    // Skip auth check for public routes
    if (isPublicRoute()) return;

    if (!token || !userDetail) {
      dispatch(setUser(null));
      sessionStorage.clear();
      navigate("/login");
      return;
    }

    try {
      const decoded = jwtDecode(token);
      const now = Date.now() / 1000;

      if (decoded.exp < now) {
        // Start logout countdown
        let seconds = 3;
        setCountdown(seconds);

        const interval = setInterval(() => {
          seconds--;
          setCountdown(seconds);
          if (seconds <= 0) {
            clearInterval(interval);
            dispatch(setUser(null));
            sessionStorage.clear();
            navigate("/login");
          }
        }, 1000);

        showToast(
          `Your session has expired.\nRedirecting to Login page in ${seconds}s...`,
          "error"
        );
      }
    } catch (err) {
      console.error("Invalid token:", err);
      dispatch(setUser(null));
      sessionStorage.clear();
      navigate("/login");
    }
  };

  // check token on every route change
  useEffect(() => {
    checkTokenValidity();
  }, [location.pathname]);

  // periodic silent check (every 10 mins)
  useEffect(() => {
    const interval = setInterval(checkTokenValidity, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // live countdown toast updates
  useEffect(() => {
    if (countdown === null) return;
    if (countdown > 0) {
      showToast(
        `Your session has expired.\nRedirecting to Login page in ${countdown}s...`,
        "error",
        "top",
        true,
        3500
      );
    }
  }, [countdown]);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-grow section-center">
        <Outlet />
      </main>
      <Footer />
      <ToastContainer />
    </div>
  );
};

export default Layout;
